/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { AzureSkuInfo } from 'src/core/services/azure/azureSwaggers';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { Region } from 'src/core/types/regionTypes';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

type AzureResourcesManagementParams = {
    subscriptionId: string;
    skuStrategy?: (sku: AzureSkuInfo) => boolean;
    regionStrategy?: (region: AzureSkuInfo) => boolean;
    resourceStrategy?: (resource: AzureCsAccountStore) => boolean;
    subscriptionStrategy?: (subscription: AzureSubscriptionStore) => boolean;
    configs?: { resourceGroups?: boolean; resources?: boolean; skuAndRegions?: boolean };
};

export const useAzureResourcesManagement = (params: AzureResourcesManagementParams) => {
    const {
        subscriptionId,
        skuStrategy = () => true,
        regionStrategy = () => true,
        resourceStrategy = () => true,
        subscriptionStrategy = () => true,
        configs = { resourceGroups: false, resources: true, skuAndRegions: false }
    } = params;
    const { dispatcher, rootStore } = useGlobalState();
    const resourcesStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
    const skusStoreHouse = useLocalStore<ResourceStoreHouse<{ id: string } & AzureSkuInfo>>(createResourceStoreHouse);

    // Whenever a subscription changes refetch the following for the newly selected subscription:
    //     - Resources.
    //     - Resource groups
    //     - Allowed SKUs and regions
    React.useEffect(() => {
        if (subscriptionId) {
            if (configs.resourceGroups) {
                dispatcher.dispatch('getAzureResourceGroups', { id: subscriptionId });
            }

            if (configs.skuAndRegions) {
                dispatcher.dispatch('getAllowedSkusBySubscription', { subscriptionId, localStore: skusStoreHouse });
            }

            if (configs.resources) {
                dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
                    id: params.subscriptionId,
                    localStoreHouse: resourcesStoreHouse
                });
            }
        }
    }, [subscriptionId, configs.resourceGroups, configs.resources, configs.skuAndRegions]);

    const reloadResources = React.useCallback(
        () =>
            dispatcher.dispatch('getAzureCsAccountsBySubscriptionId', {
                id: subscriptionId,
                filterPredicate: resourceStrategy,
                localStoreHouse: resourcesStoreHouse
            }),
        [subscriptionId]
    );

    return {
        // Trackers.
        skusTrackers: skusStoreHouse.tracker,
        regionsTrackers: skusStoreHouse.tracker,
        resourcesTracker: resourcesStoreHouse.tracker,
        tenantsTracker: rootStore.azureStore.tenantStoreHouse.tracker,
        subscriptionTracker: rootStore.azureStore.subscriptionStoreHouse.tracker,
        resourceGroupsTracker: rootStore.azureStore.resourceGroupStoreHouse.tracker,

        // Data fields.
        tenants: rootStore.azureStore.tenantStoreHouse.items,
        resources: resourcesStoreHouse.items.filter(resourceStrategy),
        resourceGroups: rootStore.azureStore.resourceGroupStoreHouse.items,
        subscriptions: rootStore.azureStore.subscriptionStoreHouse.items.filter(subscriptionStrategy),
        regions: skusStoreHouse.items
            .filter(regionStrategy)
            .reduce<Region[]>((acc, item) => [...acc, ...item.locations], [])
            .filter((region, index, arr) => arr.indexOf(region) === index),
        skus: skusStoreHouse.items
            .filter(skuStrategy)
            .filter((sku, index, arr) => arr.findIndex(s => s.name === sku.name) === index)
            .map(item => ({ code: item.name, tier: item.tier })),

        // Store houses
        resourcesStoreHouse,

        // Util methods
        reloadResources
    };
};
